import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface OtfExpression {
  expression: string;
  alias: string;
  raw: string;
}

export interface OtfComputedResult {
  alias: string;
  value: number | string | null;
}

@Injectable({ providedIn: 'root' })
export class OnTheFlyComputationService {
  private widgetExpressions = new Map<string, OtfExpression[]>();
  private stateSubject = new BehaviorSubject<Map<string, OtfExpression[]>>(new Map());

  readonly state$ = this.stateSubject.asObservable();

  setExpressions(widgetId: string, expressions: OtfExpression[]): void {
    if (expressions.length === 0) {
      this.widgetExpressions.delete(widgetId);
    } else {
      this.widgetExpressions.set(widgetId, [...expressions]);
    }
    this.stateSubject.next(new Map(this.widgetExpressions));
  }

  getExpressions(widgetId: string): OtfExpression[] {
    return this.widgetExpressions.get(widgetId) ?? [];
  }

  clearExpressions(widgetId: string): void {
    this.widgetExpressions.delete(widgetId);
    this.stateSubject.next(new Map(this.widgetExpressions));
  }

  hasExpressions(widgetId: string): boolean {
    return (this.widgetExpressions.get(widgetId)?.length ?? 0) > 0;
  }

  /**
   * Parses "some expression as alias" into structured form.
   * Returns null if the format is invalid.
   */
  parseExpression(raw: string): OtfExpression | null {
    const trimmed = raw.trim();
    const match = trimmed.match(/^(.+?)\s+as\s+(\w+)$/i);
    if (!match) return null;
    return {
      expression: match[1].trim(),
      alias: match[2].trim(),
      raw: trimmed,
    };
  }

  /**
   * Evaluates an expression against a single data row.
   *
   * Supports:
   *   - Bare column names:    revenue * 1.1
   *   - Bracketed names:      [unit price] * 0.9   (for names with spaces)
   *   - Standard arithmetic:  +  -  *  /  %  **
   *   - Math functions:       Math.round(), Math.abs(), Math.sqrt(), etc.
   *   - Ternary:              quantity > 0 ? revenue / quantity : 0
   *
   * Returns null when the expression cannot be evaluated.
   */
  evaluateRowExpression(
    expression: string,
    row: Record<string, unknown>,
  ): number | string | null {
    let expr = expression;

    // 1. Replace [Column Name] bracket notation (handles names with spaces)
    expr = expr.replace(/\[([^\]]+)\]/g, (_, colName) => {
      return this.toExpressionLiteral(row[colName]);
    });

    // 2. Replace bare column names — longest first to avoid partial matches
    const cols = Object.keys(row).sort((a, b) => b.length - a.length);
    for (const col of cols) {
      if (/\s/.test(col)) continue; // already handled by bracket notation above
      const escaped = col.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr.replace(
        new RegExp(`\\b${escaped}\\b`, 'g'),
        this.toExpressionLiteral(row[col]),
      );
    }

    try {
      // eslint-disable-next-line no-new-func
      const result = new Function(`"use strict"; return (${expr})`)();
      if (result === null || result === undefined || (typeof result === 'number' && isNaN(result))) {
        return null;
      }
      return result as number | string;
    } catch {
      return null;
    }
  }

  /**
   * Applies expressions to every row, returning new rows with computed columns appended.
   * Always starts from the provided baseRows so expressions are never compounded across calls.
   */
  applyExpressionsToRows(
    baseRows: Record<string, unknown>[],
    expressions: OtfExpression[],
    sliderValue?: number,
  ): Record<string, unknown>[] {
    return baseRows.map((row) => {
      const newRow: Record<string, unknown> = { ...row };
      if (sliderValue !== undefined) newRow['sliderValue'] = sliderValue;
      for (const expr of expressions) {
        newRow[expr.alias] = this.evaluateRowExpression(expr.expression, newRow);
      }
      return newRow;
    });
  }

  /**
   * Evaluates all expressions against a single row and returns label:value results.
   * Used for the live preview panel in the drawer.
   */
  evaluatePreviewRow(
    expressions: OtfExpression[],
    row: Record<string, unknown>,
    sliderValue: number,
  ): OtfComputedResult[] {
    const rowWithSlider = { ...row, sliderValue };
    return expressions.map((comp) => ({
      alias: comp.alias,
      value: this.evaluateRowExpression(comp.expression, rowWithSlider),
    }));
  }

  private toExpressionLiteral(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    const num = Number(value);
    if (!isNaN(num) && value !== '') return String(num);
    return JSON.stringify(String(value));
  }
}
